import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import ReactECharts from "echarts-for-react";
import { getRegionalanalyseResult } from "../../selectors/widget.js";
import Block from "../Block";
import Select from "react-select";
import { round } from "lodash";

const Graph = ({ module, regionIds }) => {
  const { dataset, leftDropDown, rightDropDown, series, cityIndex, valueType, tooltipFormat, axisLabelFormat } = module;
  const apiResult = useSelector((state) => getRegionalanalyseResult(state, dataset));
  const [mergedOptions, setMergedOptions] = useState(null);

  const [leftDropDownOptions, setLeftDropDownOptions] = useState(null);
  const [selectedLeftValue, setSelectedLeftValue] = useState(null);

  const [selectedRightValue, setSelectedRightValue] = useState(null);

  const [apiDataset, setApiDataset] = useState(null);
  const [filteredDataset, setFilteredDataset] = useState(null);

  useEffect(() => {
    if (apiResult) {
      let result = JSON.parse(apiResult);
      let ids = regionIds.map((item) => item.nr);
      let filtered = result.response.filter((item) => ids.includes(item.nr) || item.navn === "Median");
      let sorted = filtered.sort((a, b) => (a.aar > b.aar ? 1 : -1));
      setApiDataset(sorted);
    }
  }, [apiResult, regionIds]);

  useEffect(() => {
    if (apiDataset && leftDropDown) {
      if (leftDropDown === "places") {
        let filtered = apiDataset.filter((item) => item.nr !== cityIndex);
        const unique = [...new Set(filtered.map((item) => item.navn))];
        let sorted = unique.sort((a, b) => (a > b ? 1 : -1));
        let parsed = parseOptions(sorted);
        setLeftDropDownOptions(parsed);
      }
    }
  }, [apiDataset, leftDropDown, cityIndex]);

  useEffect(() => {
    if (filteredDataset) {
      let data = filteredDataset;
      let seriesData = [];
      let minValue = null;
      let maxValue = null;
      if (series) {
        seriesData = series.map((item) => {
          let seriesItem = data.filter((i) => {
            if (item.group === "leftDropDown") {
              if (selectedLeftValue) {
                return i.navn === selectedLeftValue.value;
              }
            }

            if (typeof item.group === "string" || item.group instanceof String) {
              return i.navn === item.group;
            }
            return i.nr === cityIndex;
          });
          let seriesItemData = seriesItem.map((i) => {
            if (item.value === "rightDropDown") {
              if (selectedRightValue) {
                return round(i[selectedRightValue.value], 2);
              }
            }
            if (valueType === "percentage") {
              return {
                name: new Date(i.fullyear, i.quarter * 3 - 3, 1),
                value: [`${i.fullyear}/${i.quarter * 3 - 2}/1`, i[item.value] * 100, i.aar],
              };
            }

            return {
              name: new Date(i.fullyear, i.quarter * 3 - 3, 1),
              value: [`${i.fullyear}/${i.quarter * 3 - 2}/1`, i[item.value], i.aar],
            };
          });

          if (seriesItemData && seriesItemData.length > 0) {
            let seriesValues = seriesItemData.map((i) => i.value[1]);
            if (minValue === null) {
              minValue = seriesValues.reduce((prev, curr) => (prev < curr ? prev : curr));
            } else {
              let min = seriesValues.reduce((prev, curr) => (prev < curr ? prev : curr));
              if (min < minValue) {
                minValue = min;
              }
            }
            if (maxValue === null) {
              maxValue = seriesValues.reduce((prev, curr) => (prev > curr ? prev : curr));
            } else {
              let max = seriesValues.reduce((prev, curr) => (prev > curr ? prev : curr));
              if (max > maxValue) {
                maxValue = max;
              }
            }
          }
          return {
            data: seriesItemData,
            type: "line",
            name: seriesItem[0] ? seriesItem[0].navn : "",
            smooth: true,
            showSymbol: false,
          };
        });
      }

      let nObj = {
        ...JSON.parse(options),
        tooltip: {
          trigger: "axis",
          order: "valueDesc",
          formatter: function (params) {
            let tooltip = `${params[0].value[2]} <br/>`;
            params.forEach((item) => {
              if (!tooltipFormat) {
                tooltip += `${item.marker} ${item.seriesName}: ${item.value} <br/>`;
              }
              switch (tooltipFormat.type) {
                case "label":
                  tooltip += `${item.marker} ${tooltipFormat.label}: ${item.value[1]}<br/>`;
                  break;
                default:
                  tooltip += `${item.marker} ${item.seriesName}: ${item.value[1]} <br/>`;
                  break;
              }
            });
            return tooltip;
          },
        },
        legend: {
          show: true,
          data: seriesData.map((item) => item.name),
        },
        xAxis: {
          type: "time",
          splitNumber: 9,
          max: new Date(),
          axisLabel: {
            interval: 0,
            rotate: 90,
          },
        },
        yAxis: {
          type: "value",
          min: caclulateBuffer(minValue, valueType, "-"),
          max: caclulateBuffer(maxValue, valueType, "+"),
          axisLabel: {
            show: true,
            formatter: null,
          },
        },
        series: seriesData,
      };
      if (axisLabelFormat) {
        if (axisLabelFormat === "percentage") {
          nObj.yAxis.axisLabel = {
            formatter: function (value) {
              return value + "%";
            },
          };
        }
      }
      setMergedOptions(nObj);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredDataset, selectedRightValue, selectedLeftValue]);

  useEffect(() => {
    if (selectedLeftValue && leftDropDown === "places") {
      const filterPeriod = (place) => {
        let filtered = apiDataset.filter(
          (item) => item.navn === place || item.nr === cityIndex || item.navn === "Median"
        );
        setFilteredDataset(filtered);
      };
      filterPeriod(selectedLeftValue.value);
    }
  }, [selectedLeftValue, apiDataset, leftDropDown, cityIndex]);

  useEffect(() => {
    if (leftDropDownOptions && !selectedLeftValue) {
      setSelectedLeftValue(leftDropDownOptions.filter((item) => item.value === "Norge")[0]);
    }
  }, [leftDropDownOptions, selectedLeftValue]);

  useEffect(() => {
    if (rightDropDown && !selectedRightValue) {
      setSelectedRightValue(rightDropDown[0]);
    }
  }, [rightDropDown, selectedRightValue]);

  useEffect(() => {
    if (apiDataset && !rightDropDown) {
      setSelectedRightValue(null);
    }
  }, [apiDataset, rightDropDown]);

  useEffect(() => {
    if (apiDataset && !leftDropDown) {
      setLeftDropDownOptions(null);
      setSelectedLeftValue(null);
      setFilteredDataset(apiDataset);
    }
  }, [apiDataset, leftDropDown]);

  if (mergedOptions) {
    return (
      <Block>
        <div className="selectbox">
          {leftDropDown && leftDropDownOptions && (
            <div className="col-lg-6">
              <Select
                options={leftDropDownOptions}
                defaultValue={leftDropDownOptions[0]}
                onChange={(d) => {
                  setSelectedLeftValue(d);
                }}
                value={selectedLeftValue}
              />
            </div>
          )}

          {rightDropDown && (
            <div className="col-lg-6">
              <Select
                options={rightDropDown}
                defaultValue={rightDropDown[0]}
                onChange={(d) => {
                  setSelectedRightValue(d);
                }}
                value={selectedRightValue}
              />
            </div>
          )}
        </div>

        <ReactECharts option={mergedOptions} style={{ marginTop: "20px", height: "400px" }} notMerge={true} />
      </Block>
    );
  }

  return null;
};
export default Graph;

const options = JSON.stringify({
  grid: { top: 40, right: 20, bottom: 2, left: 40, containLabel: true },
  // xAxis: {
  //   type: 'category',
  //   data: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
  // },
  yAxis: {
    type: "value",
  },
  series: null,
  tooltip: {
    trigger: "item",
    formatter: `{c}%`,
  },
});

const parseOptions = (options) => {
  return options.map((option) => Object.assign({}, { label: option, value: option }));
};

const caclulateBuffer = (number, valueType, operator) => {
  let buffer = 0;

  if (valueType === "percentage") {
    if (number <= 2) {
      buffer = 0.1;

      if (operator === "-") {
        let value = round(number - buffer, 3);
        if (value < 0.1 && value > 0) {
          return 0;
        }
        return value;
      }
      return round(number + buffer, 3);
    }
  }
  if (number > 10 && number <= 200) {
    buffer = 5;
  } else if (number > 200 && number <= 500) {
    buffer = 50;
  } else if (number > 500 && number <= 1000) {
    buffer = 100;
  } else if (number > 1000) {
    buffer = 1000;
  } else if (number <= 10) {
    buffer = 1;
  }

  if (operator === "-") {
    return round((number - buffer) / buffer) * buffer;
  }
  return round((number + buffer) / buffer) * buffer;
};
